// components/GuideVueAdmin.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, HelpCircle, Users, CheckCircle, Fingerprint, AlertTriangle,
  Send, Search, Filter, ChevronDown, ChevronUp,
  Info, BookOpen, ClipboardList, Clock, XCircle, RotateCcw, ChevronRight
} from 'lucide-react';

const COLOR = '#b08d1a';

const getWorkflowSteps = () => [
  {
    num: '1',
    title: 'Sélectionner la date',
    desc: "Utilisez le sélecteur de date en haut de l'écran pour choisir la journée concernée. Par défaut, la date du jour est sélectionnée. Vous pouvez régulariser des journées passées si nécessaire.",
  },
  {
    num: '2',
    title: 'Rechercher ou filtrer',
    desc: "Utilisez la barre de recherche pour localiser un employé par nom, badge ou département. Le filtre par département permet d'afficher uniquement les membres d'un service précis.",
  },
  {
    num: '3',
    title: 'Enregistrer les pointages',
    desc: "Cliquez sur les boutons Arrivée, Sortie Pause, Retour Pause et Départ pour chaque employé concerné. L'heure actuelle est enregistrée automatiquement. Les pointages s'accumulent en mémoire avant validation.",
    conseil: 'Vous pouvez annuler un pointage en cliquant sur la croix à côté de l\'heure, avant d\'avoir validé.'
  },
  {
    num: '4',
    title: 'Déclarer les absences',
    desc: "Cliquez sur le bouton Absent dans la ligne de l'employé concerné. Tous les boutons de pointage sont désactivés pour cet employé. L'absence est ajoutée à la liste des pointages en attente.",
    conseil: 'Un employé ayant déjà un pointage d\'arrivée ou de départ ne peut plus être déclaré absent.'
  },
  {
    num: '5',
    title: 'Ajouter des observations',
    desc: "Le champ Observations permet d'ajouter une note libre sur l'employé concerné. Ce champ n'est actif que si au moins un pointage est en cours pour cet employé.",
    conseil: 'Les observations sont visibles dans l\'historique et apparaissent dans les rapports PDF.'
  },
  {
    num: '6',
    title: 'Valider',
    desc: "Cliquez sur le bouton Enregistrer individuel sur la ligne de l'employé pour une sauvegarde immédiate, ou sur le bouton Enregistrer global en bas du tableau pour sauvegarder tous les pointages en attente en une seule opération.",
    conseil: 'Ne quittez pas la page avant d\'avoir validé - les pointages en attente non enregistrés sont perdus.'
  },
];

const getColonnesGuide = (modePause, comptaEnabled) => [
  {
    color: '#448696',
    label: 'Employé',
    desc: "Nom, prénom et poste de l'employé. Chaque ligne correspond à un employé actif dans votre organisation."
  },
  {
    color: '#718096',
    label: 'Département',
    desc: "Service auquel appartient l'employé. Permet d'identifier rapidement les membres d'une même équipe."
  },
  {
    color: '#38a169',
    label: 'Arrivée',
    desc: "Affiche l'heure d'arrivée si déjà enregistrée, ou un bouton pour pointer l'arrivée. Le badge de ponctualité indique si l'employé est à temps, en tolérance ou en retard.",
    exemple: "Arrivée à 07h45 avec heure prévue à 07h30 - le badge affiche Tolérance ou Retard selon la configuration."
  },
  ...(modePause ? [
    {
      color: '#d97706',
      label: 'Sortie Pause',
      desc: "Disponible uniquement en mode 4 pointages. Enregistre l'heure à laquelle l'employé part en pause.",
    },
    {
      color: '#7c3aed',
      label: 'Retour Pause',
      desc: "Disponible uniquement en mode 4 pointages. Enregistre le retour de l'employé après sa pause. Si la durée dépasse le seuil configuré, un badge Pause excessive apparaît.",
    }
  ] : []),
  {
    color: '#3182ce',
    label: 'Départ',
    desc: "Affiche l'heure de départ si déjà enregistrée, ou un bouton pour pointer le départ. Le badge indique si le départ est normal, anticipé ou très anticipé.",
    exemple: "Un employé partant avant l'heure aura un badge Départ anticipé ou Départ très anticipé."
  },
  {
    color: '#e53e3e',
    label: 'Absent',
    desc: "Permet de déclarer un employé absent pour la journée. Une fois marqué absent, tous les boutons de pointage sont désactivés. L'absence peut être annulée avant validation.",
    conseil: 'Réservez ce bouton aux absences non planifiées. Les congés doivent être gérés dans le module RH dédié.'
  },
  {
    color: '#718096',
    label: 'Observations',
    desc: "Champ texte libre pour documenter une note sur un pointage en cours. Actif uniquement si un pointage est en attente pour cet employé.",
    conseil: 'Utilisez ce champ pour toute information contextuelle utile à la comptabilité ou aux RH.'
  },
  ...(comptaEnabled ? [{
    color: '#6366f1',
    label: 'Comptabilité',
    desc: "Permet d'envoyer les pointages d'un employé vers le module comptabilité. Un pointage envoyé affiche Envoyé et ne peut plus être modifié.",
    conseil: 'Transmettez uniquement les pointages vérifiés et validés.'
  }] : []),
];

const getPonctualiteGuide = () => [
  { label: 'À temps', color: '#38a169', bg: '#f0fff4', desc: "L'employé a pointé dans les horaires prévus, avant la limite de tolérance." },
  { label: 'Tolérance', color: '#d97706', bg: '#fffbeb', desc: "L'employé est légèrement en retard, dans la marge de tolérance configurée." },
  { label: 'En retard', color: '#c05621', bg: '#fff7ed', desc: "L'employé a dépassé la tolérance mais reste dans la plage d'arrivée autorisée." },
  { label: 'Retard grave', color: '#e53e3e', bg: '#fff5f5', desc: "L'employé a pointé très tard, bien au-delà de la tolérance. Ce cas nécessite une justification dans l'historique." },
  { label: 'Départ anticipé', color: '#d97706', bg: '#fffbeb', desc: "L'employé est parti légèrement avant l'heure de fin prévue, dans la marge de tolérance." },
  { label: 'Départ très anticipé', color: '#e53e3e', bg: '#fff5f5', desc: "L'employé est parti bien avant l'heure de fin. Ce cas nécessite une justification dans l'historique." },
  { label: 'Pause excessive', color: '#e53e3e', bg: '#fff5f5', desc: "La durée de pause a dépassé le seuil maximal configuré. Visible uniquement en mode 4 pointages." },
];

const AccordionSection = ({ title, colorBar, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: colorBar }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBar + '15' }}>
            <Icon size={15} style={{ color: colorBar }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-2 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

const GuideCard = ({ color, label, desc, exemple, conseil }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="flex items-start gap-3">
      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
        {exemple && (
          <p className="text-xs text-gray-400 mt-1.5">
            <span className="font-semibold text-gray-500">Ex :</span> {exemple}
          </p>
        )}
        {conseil && (
          <p className="text-xs mt-1.5 font-medium" style={{ color: COLOR }}>{conseil}</p>
        )}
      </div>
    </div>
  </div>
);

const WorkflowStep = ({ num, title, desc, conseil }) => (
  <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xs"
      style={{ backgroundColor: COLOR }}
    >
      {num}
    </div>
    <div className="flex-1">
      <p className="text-sm font-semibold text-gray-900 mb-1">{title}</p>
      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
      {conseil && (
        <p className="text-xs mt-1.5 font-medium" style={{ color: COLOR }}>{conseil}</p>
      )}
    </div>
  </div>
);

const GuideVueAdmin = ({ onClose, modePause = 'automatique', comptaEnabled = false }) => {
  const workflowSteps = getWorkflowSteps();
  const colonnesGuide = getColonnesGuide(modePause === 'pointage', comptaEnabled);
  const ponctualiteGuide = getPonctualiteGuide();

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div
          className="flex-shrink-0 px-6 py-5 flex items-center justify-between"
          style={{ backgroundColor: COLOR }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <ClipboardList size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">Guide de lecture</h2>
              <p className="text-xs text-white/70 mt-0.5">Pointage administrateur</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <Info size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              La Vue Admin est réservée aux responsables. Elle permet de pointer manuellement les employés, de déclarer des absences et de gérer les pointages du jour.
              {modePause === 'pointage' && <span className="font-semibold text-gray-700"> Configuration actuelle : 4 pointages par jour.</span>}
              {modePause === 'automatique' && <span className="font-semibold text-gray-700"> Configuration actuelle : 2 pointages par jour.</span>}
            </p>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          <AccordionSection title="Utilisation étape par étape" colorBar={COLOR} icon={ClipboardList} defaultOpen={true}>
            {workflowSteps.map((step) => (
              <WorkflowStep key={step.num} {...step} />
            ))}
          </AccordionSection>

          <AccordionSection title="Les colonnes du tableau" colorBar="#448696" icon={Users} defaultOpen={false}>
            {colonnesGuide.map((col) => (
              <GuideCard key={col.label} {...col} />
            ))}
          </AccordionSection>

          <AccordionSection title="Les badges de ponctualité" colorBar="#e53e3e" icon={Clock} defaultOpen={false}>
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-100 mb-2">
              <p className="text-xs text-blue-600 leading-relaxed">
                Les badges sont calculés automatiquement selon les plages horaires configurées dans les paramètres de pointage. Ils apparaissent à côté de l'heure enregistrée.
              </p>
            </div>
            {ponctualiteGuide.map((item) => (
              <div key={item.label} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/30">
                <div
                  className="px-2.5 py-1 rounded text-xs font-semibold flex-shrink-0 whitespace-nowrap"
                  style={{ backgroundColor: item.bg, color: item.color, border: `1px solid ${item.color}30` }}
                >
                  {item.label}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed pt-0.5">{item.desc}</p>
              </div>
            ))}
          </AccordionSection>

          <AccordionSection title="Pointage individuel et en lot" colorBar="#6366f1" icon={Send} defaultOpen={false}>

            <GuideCard
              color="#448696"
              label="Mode photo anti-fraude activé"
              desc="Lorsque la vérification photo est activée dans les paramètres, le bouton de pointage manuel (fingerprint) n'est pas affiché dans la Vue Employé. Le pointage se déclenche automatiquement après la prise de photo. L'employé scanne ou saisit son numéro de badge, le popup caméra s'ouvre, et dès que la photo est validée le pointage est enregistré sans action supplémentaire."
              conseil="Ce mode est recommandé pour renforcer la fiabilité des présences et éviter les pointages frauduleux."
            />
            <GuideCard
              color="#6366f1"
              label="Pointage en lot"
              desc="Pointez plusieurs employés successivement - les saisies s'accumulent en mémoire. Cliquez ensuite sur le bouton Enregistrer global en bas du tableau pour tout sauvegarder en une seule opération. Recommandé pour 5 employés ou plus."
              conseil="Le compteur en bas à gauche indique le nombre de pointages en attente de validation."
            />
            <GuideCard
              color="#38a169"
              label="Pointage individuel"
              desc="Cliquez sur l'icône de validation directement sur la ligne de l'employé pour sauvegarder immédiatement ses pointages sans affecter les autres. Recommandé pour 1 ou 2 employés."
            />
            <GuideCard
              color="#e53e3e"
              label="Bouton Réinitialiser"
              desc="Efface tous les pointages en attente non encore enregistrés. N'affecte pas les pointages déjà sauvegardés en base de données."
              conseil="Utilisez ce bouton uniquement si vous souhaitez recommencer entièrement votre saisie."
            />
            <GuideCard
              color="#448696"
              label="Pagination - 10 employés par page"
              desc="La Vue Admin affiche au maximum 10 employés par page. Naviguez entre les pages via les boutons en bas du tableau."
              conseil="Attention : changer de page vide automatiquement les pointages en attente non enregistrés. Validez toujours avant de changer de page."
            />
          </AccordionSection>

          {/* Contexte d'utilisation */}
          <div className="px-5 py-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-2">Quand utiliser la Vue Admin</p>
            <div className="space-y-1.5">
              {[
                "Le kiosque de pointage est en panne ou inaccessible",
                "Un employé est sur un site distant sans accès au système",
                "Un employé a oublié de pointer et nécessite une régularisation",
                "Une absence non planifiée doit être déclarée pour un ou plusieurs employés"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: COLOR }} />
                  <p className="text-xs text-gray-500">{item}</p>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Mode : <span className="font-semibold text-gray-600">
              {modePause === 'pointage' ? '4 pointages / jour' : '2 pointages / jour'}
            </span>
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Fermer
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GuideVueAdmin;